import { DateTime } from 'luxon';
import type { TripLegResponseItem } from '../data/models/trip-leg.model';
import { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import { tripStatusNoLoadList } from '../data/models/trip-leg-status.model';

/**
 * Checks if a trip leg has all required timestamps based on its status.
 *
 * Required timestamps vary by status:
 * - Completed trips: onSite, onBoard, and completed
 * - Canceled late trips: canceledLate
 * - No load trips: onSite and noLoad
 *
 * @param tripLeg - The trip leg to check
 * @returns true if all required timestamps are present, false otherwise
 */
export function checkTimestampsFilled(
  tripLeg: Pick<TripLegResponseItem, 'tripLegStatusLogs' | 'tripLegStatusBC'>,
): boolean {
  const statusLogs = tripLeg.tripLegStatusLogs;
  if (!statusLogs) return false;

  const currentStatus = tripLeg.tripLegStatusBC as TripLegStatusBC;

  const hasOnsite = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.Onsite,
  );
  const hasOnboard = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.Onboard,
  );
  const hasCompleted = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.Completed,
  );
  const hasCanceledLate = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.CanceledLate,
  );
  const hasNoLoad = statusLogs.find(({ tripLegStatusBC }) =>
    tripStatusNoLoadList.includes(tripLegStatusBC as TripLegStatusBC),
  );

  if (tripStatusNoLoadList.includes(currentStatus)) {
    return Boolean(hasOnsite && hasNoLoad);
  }
  if (currentStatus === TripLegStatusBC.CanceledLate) {
    return Boolean(hasCanceledLate);
  }
  return Boolean(hasOnsite && hasOnboard && hasCompleted);
}

/**
 * Compares two timestamps to minute precision.
 *
 * @param optimisticTime - The optimistic timestamp (ISO string)
 * @param backendTime - The backend timestamp (ISO string)
 * @returns true if timestamps match to the minute, or if either is missing
 */
export function timestampsMatchToMinute(
  optimisticTime: string | undefined,
  backendTime: string | undefined,
): boolean {
  if (!optimisticTime || !backendTime) {
    return true;
  }
  const backendDateTime = DateTime.fromISO(backendTime);
  const optimisticDateTime = DateTime.fromISO(optimisticTime);
  return (
    backendDateTime.startOf('minute').toISO() ===
    optimisticDateTime.startOf('minute').toISO()
  );
}

/**
 * Checks if a trip leg is ready to be invoiced.
 *
 * A trip leg is ready to invoice if it has:
 * - A driver
 * - A status
 * - All required timestamps filled
 *
 * @param tripLeg - The trip leg to check
 * @returns true if ready to invoice, false otherwise
 */
export function checkTripLegReadyToInvoice(
  tripLeg: Pick<
    TripLegResponseItem,
    | 'driverFirstName'
    | 'driverLastName'
    | 'tripLegStatusBC'
    | 'tripLegStatusLogs'
  >,
): boolean {
  if (
    !tripLeg.driverFirstName ||
    !tripLeg.driverLastName ||
    !tripLeg.tripLegStatusBC
  ) {
    return false;
  }

  return checkTimestampsFilled(tripLeg);
}
